import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from "axios";
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from "components/layout/PraxStyledSegment";
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from "components/layout/StyledViewTableRow";
import VirtualizedTable from "components/layout/VirtualizedTable";
import SearchResultComponent from 'components/SearchResultComponent';
import {
  InterventionUpsertRefDataContextProvider
} from 'components/settings/intervention/InterventionUpsertRefDataContext';
import UpsertInterventionForm from 'components/settings/intervention/UpsertInterventionForm';
import { FormState } from 'final-form';
import _ from 'lodash';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Dimmer, Grid, Icon, Loader, Message, Modal } from "semantic-ui-react";
import { deleteIntervention, findInterventionById, searchInterventions } from "services/interventionServices";
import { getAllQuestionSets } from 'services/questionSetsServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { InterventionDto, InterventionSearchDto, QuestionSetDto, UpsertInterventionDto } from "ts-types/api.types";
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';

const TopBox = styled(PraxStyledSegment)`
  .actions-row.right {
    text-align: right;

    .trigger-wrapper {
      display: inline-block;
    }

    button {
      margin-left: 0.7rem;
    }
  }
`;

const initialInterventionSearch: Partial<InterventionSearchDto> = {};

interface InterventionFieldProps extends FormState {
  values: Partial<InterventionSearchDto>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  foundInterventions: Array<InterventionDto>;
  interventionsLoaded: boolean;
  intervention?: UpsertInterventionDto;
  interventionId?: number;
  questionSets: QuestionSetDto[],
  questionSetMap: { [id: number]: QuestionSetDto };
  addEditModalOpen: boolean;
  loading: boolean;
  searchValues: Partial<InterventionSearchDto>;
  cancelTokenSource: CancelTokenSource;
  errorMessage?: String;
}

class InterventionView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundInterventions: [],
      interventionsLoaded: false,
      intervention: undefined,
      interventionId: undefined,
      questionSets: [],
      questionSetMap: [],
      addEditModalOpen: false,
      loading: false,
      searchValues: initialInterventionSearch,
      cancelTokenSource
    };

    getAllQuestionSets(cancelTokenSource).then(response => {
          const questionSetMap = _.keyBy(response, "id");
          this.setState({
            questionSets: response,
            questionSetMap
          });
        })
        .catch(noop);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findInterventionById(id, cancelTokenSource)
          .then(response => {
            this.showModal(id, response);
          })
          .catch(noop);
    }
  };

  showModal = (id?: number, intervention?: UpsertInterventionDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        intervention: intervention,
        interventionId: id
      };
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      interventionId: undefined,
      intervention: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues} = this.state;

    this.fetchInterventions(searchValues);
    this.hideModal();
  }

  deleteIntervention = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteIntervention(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundInterventions}: State) => ({
            foundInterventions: foundInterventions.filter(
                (intervention: InterventionDto) => intervention.id != response
            )
          }));
        })
        .catch((error) => this.handleError(error.response.data));
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors = [errorUtils.deletingReferencedInterventionNotAllowed];

    if (knownErrors.includes(errorCode)) {
      this.setErrorMessage(getMessage(`intervention.error.${errorCode}`))
    } else {
      this.setErrorMessage(getMessage('error.general'))
    }
  }

  setErrorMessage = (error?: String) => {
    this.setState({
      errorMessage: error
    })
  }

  fetchInterventions = (values: any) => {

    const {cancelTokenSource} = this.state;
    this.setState({interventionsLoaded: false});

    const onFinally = () => {
      this.setState({
        interventionsLoaded: true
      })
    };

    searchInterventions(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundInterventions: response,
              searchValues: values
            })
        )
        .catch((error) => this.handleError(error.response.data))
        .finally(onFinally);
  }

  handleChange = debounce(({values}: InterventionFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    this.fetchInterventions(values);
  }, 300);


  render(): React.ReactNode {
    const {interventionsLoaded} = this.state;
    return (
        <TopBox>

          <div>
            {this.renderSearchInterventionsSegment()}
          </div>

          <StyledViewTableRow>
            {!interventionsLoaded
                ? this.renderLoader()
                : this.renderInterventionsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertInterventionModal()}
        </TopBox>
    );
  }

  renderSearchInterventionsSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              includingInactive: false
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {errorMessage, foundInterventions} = this.state;

    const numResults = foundInterventions.length;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="interventionSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"middle"}>
              <Field
                  name="includingInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9} className="actions-row right">
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("intervention.loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderInterventionsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundInterventions} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundInterventions.length}
            rowGetter={this.interventionRowGetter}
            rowRenderer={this.foundInterventionsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: getMessage("intervention.description"),
                dataKey: "description",
                cellRenderer: this.interventionDescriptionCellRendered
              },
              {
                width: 200,
                label: getMessage("intervention.interventionType"),
                dataKey: "type",
                cellRenderer: this.interventionTypeCellRenderer
              },
              {
                width: 200,
                flexGrow: 1,
                label: getMessage("intervention.externalCode"),
                dataKey: "externalCode",
                cellRenderer: this.externalCodeCellRenderer
              },
              {
                width: 300,
                label: getMessage("intervention.questionSet"),
                dataKey: "questionSetId",
                cellRenderer: this.questionSetCellRenderer
              },
              {
                width: 150,
                label: getMessage("intervention.durationInMinutes"),
                dataKey: "durationInMinutes"
              },
              {
                width: 300,
                flexGrow: 1,
                label: getMessage("intervention.infoText"),
                dataKey: "infotext"
              },
              {
                width: 200,
                label: getMessage("intervention.authorityNotificationRequired"),
                dataKey: "authorityNotificationRequired",
                cellRenderer: this.notificationCellRenderer
              },
              {
                width: 70,
                flexGrow: 1,
                label: getMessage("intervention.status"),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: getMessage("actions"),
                dataKey: "id",
                cellRenderer: this.actionsCellRenderer
              }
            ]}
        />
    );
  };

  interventionRowGetter = ({index}: any) => {
    const {foundInterventions} = this.state;

    Object.assign(foundInterventions[index], {index: index + 1});

    return foundInterventions[index];
  };

  foundInterventionsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};
    const {foundInterventions} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundInterventions[index].id)}
        >
          {columns}
        </div>
    );
  };

  notificationCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  }

  interventionDescriptionCellRendered = ({rowData, cellData}: any) => {
    const interventionColor = rowData.color;
    const interventionDescription = cellData ? cellData : "";

    return (
        <div>
          {interventionColor &&
              <div style={{display: "inline-flex"}}>
                <Icon
                    name="circle"
                    style={{color: `${interventionColor}`}}
                    size="small"
                    title={interventionDescription}
                />
              </div>
          }
          <span>{interventionDescription}</span>
        </div>
    )
  }

  interventionTypeCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {

      return getMessage(`enum.InterventionType.${cellData}`)
    }
  }

  externalCodeCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {

      return getMessage(`enum.InterventionExternalCode.${cellData}`)
    }
  }

  questionSetCellRenderer = ({cellData}: any) => {
    const {language} = this.props;
    const {questionSetMap} = this.state;
    if (cellData && questionSetMap[cellData] !== undefined) {
      const qs = questionSetMap[cellData];
      const nameMap: { [lang: string]: string } =
          {
            "de": qs.name,
            "en": qs.nameEn || qs.name,
            "fr": qs.nameFr || qs.name,
            "it": qs.nameIt || qs.name
          };
      return nameMap[language];
    }
  }

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("intervention.active")
    }
    return getMessage("intervention.inactive");
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("intervention.confirmDelete")}
              onConfirm={() => this.deleteIntervention(cellData)}
          />
        </React.Fragment>
    );
  };

  renderUpsertInterventionModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {addEditModalOpen, intervention, interventionId, questionSets} = this.state;

    return (
        <Modal open={addEditModalOpen} closeOnDimmerClick={false} onClose={() => this.hideModal()}>
          <Modal.Header>{getMessage("intervention.title")}</Modal.Header>
          <FormModalContentContainer>
            <InterventionUpsertRefDataContextProvider>
              <UpsertInterventionForm
                  intervention={intervention}
                  interventionId={interventionId}
                  questionSets={questionSets}
                  onSave={this.onSaveUpsertForm}
                  onCancel={this.hideModal}
              />
            </InterventionUpsertRefDataContextProvider>
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(withAuthContext(InterventionView));
